import * as React from 'react'
import { Command as CommandPrimitive } from 'cmdk'
import * as PopoverPrimitive from '@radix-ui/react-popover'
import { Check, ChevronDown, Search } from 'lucide-react'
import { cn } from '@/lib/utils'

/**
 * Figma (node 60:435 — Combobox):
 * Trigger: h-8, rounded-lg, border border-input, bg-background, pl-2.5 pr-1 py-1, gap-1.5
 *   Text: text-sm, placeholder=text-muted-foreground, selected=text-foreground
 *   ChevronDown: 12px in 24px area
 *   Invalid: border-destructive
 *   Focus: ring-2 ring-ring ring-offset-2
 *   Disabled: opacity-50
 * Popover: bg-popover, border border-border, rounded-lg, shadow-md
 * Search input: h-8, border-b border-input, px-2.5, text-sm
 * Item: rounded-md, pl-1.5 pr-8 py-1, gap-2, text-sm text-popover-foreground
 *   Hover: bg-accent text-accent-foreground
 *   Selected: Check icon (16px) absolute right-2
 */

// --- Combobox (Root = Popover) ---
// Thin wrapper instead of direct alias to avoid mutating the shared
// PopoverPrimitive.Root singleton (which would clobber IntuiPopover's displayName).

const IntuiCombobox: React.FC<
  React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Root>
> = (props) => <PopoverPrimitive.Root {...props} />
IntuiCombobox.displayName = 'IntuiCombobox'

// --- ComboboxTrigger ---

export interface IntuiComboboxTriggerProps
  extends React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Trigger> {
  invalid?: boolean
}

const IntuiComboboxTrigger = React.forwardRef<
  React.ComponentRef<typeof PopoverPrimitive.Trigger>,
  IntuiComboboxTriggerProps
>(({ className, invalid, children, ...props }, ref) => (
  <PopoverPrimitive.Trigger
    ref={ref}
    role="combobox"
    aria-haspopup="listbox"
    className={cn(
      'flex h-8 w-full items-center gap-1.5 rounded-lg border bg-background pl-2.5 pr-1 py-1 text-sm',
      'ring-offset-background',
      'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
      'disabled:cursor-not-allowed disabled:opacity-50',
      invalid ? 'border-destructive' : 'border-input',
      className
    )}
    {...props}
  >
    {children}
    <ChevronDown className="ml-auto h-3 w-3 shrink-0 opacity-50" />
  </PopoverPrimitive.Trigger>
))
IntuiComboboxTrigger.displayName = 'IntuiComboboxTrigger'

// --- ComboboxContent ---

const IntuiComboboxContent = React.forwardRef<
  React.ComponentRef<typeof PopoverPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <PopoverPrimitive.Portal>
    <PopoverPrimitive.Content
      ref={ref}
      align="start"
      sideOffset={4}
      className={cn(
        'z-[999999] w-[var(--radix-popover-trigger-width)] rounded-lg border border-border bg-popover shadow-md',
        'data-[state=open]:animate-in data-[state=closed]:animate-out',
        'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
        'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
        'data-[side=bottom]:slide-in-from-top-2 data-[side=top]:slide-in-from-bottom-2',
        className
      )}
      {...props}
    >
      <CommandPrimitive className="flex flex-col" label="Combobox">
        {children}
      </CommandPrimitive>
    </PopoverPrimitive.Content>
  </PopoverPrimitive.Portal>
))
IntuiComboboxContent.displayName = 'IntuiComboboxContent'

// --- ComboboxInput ---

const IntuiComboboxInput = React.forwardRef<
  React.ComponentRef<typeof CommandPrimitive.Input>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Input>
>(({ className, ...props }, ref) => (
  <div className="flex items-center gap-2 border-b border-input px-2.5" cmdk-input-wrapper="">
    <Search className="h-4 w-4 shrink-0 opacity-50" />
    <CommandPrimitive.Input
      ref={ref}
      className={cn(
        'flex h-8 w-full bg-transparent text-sm outline-none',
        'placeholder:text-muted-foreground',
        'disabled:cursor-not-allowed disabled:opacity-50',
        className
      )}
      {...props}
    />
  </div>
))
IntuiComboboxInput.displayName = 'IntuiComboboxInput'

// --- ComboboxList ---

const IntuiComboboxList = React.forwardRef<
  React.ComponentRef<typeof CommandPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.List>
>(({ className, ...props }, ref) => (
  <CommandPrimitive.List
    ref={ref}
    className={cn('max-h-60 overflow-y-auto p-1', className)}
    {...props}
  />
))
IntuiComboboxList.displayName = 'IntuiComboboxList'

// --- ComboboxEmpty ---

const IntuiComboboxEmpty = React.forwardRef<
  React.ComponentRef<typeof CommandPrimitive.Empty>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Empty>
>(({ className, ...props }, ref) => (
  <CommandPrimitive.Empty
    ref={ref}
    className={cn('py-6 text-center text-sm text-muted-foreground', className)}
    {...props}
  />
))
IntuiComboboxEmpty.displayName = 'IntuiComboboxEmpty'

// --- ComboboxGroup ---

const IntuiComboboxGroup = React.forwardRef<
  React.ComponentRef<typeof CommandPrimitive.Group>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Group>
>(({ className, ...props }, ref) => (
  <CommandPrimitive.Group
    ref={ref}
    className={cn(
      '[&_[cmdk-group-heading]]:px-1.5 [&_[cmdk-group-heading]]:py-1 [&_[cmdk-group-heading]]:text-sm [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground',
      className
    )}
    {...props}
  />
))
IntuiComboboxGroup.displayName = 'IntuiComboboxGroup'

// --- ComboboxItem ---

export interface IntuiComboboxItemProps
  extends React.ComponentPropsWithoutRef<typeof CommandPrimitive.Item> {
  selected?: boolean
}

const IntuiComboboxItem = React.forwardRef<
  React.ComponentRef<typeof CommandPrimitive.Item>,
  IntuiComboboxItemProps
>(({ className, selected, children, ...props }, ref) => (
  <CommandPrimitive.Item
    ref={ref}
    className={cn(
      'relative flex w-full cursor-default select-none items-center gap-2 rounded-md py-1 pl-1.5 pr-8 text-sm outline-none',
      'data-[selected=true]:bg-accent data-[selected=true]:text-accent-foreground',
      'data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50',
      className
    )}
    {...props}
  >
    {children}
    {selected && (
      <Check className="absolute right-2 h-4 w-4" />
    )}
  </CommandPrimitive.Item>
))
IntuiComboboxItem.displayName = 'IntuiComboboxItem'

// --- ComboboxSeparator ---

const IntuiComboboxSeparator = React.forwardRef<
  React.ComponentRef<typeof CommandPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <CommandPrimitive.Separator
    ref={ref}
    className={cn('-mx-1 my-1 h-px bg-border', className)}
    {...props}
  />
))
IntuiComboboxSeparator.displayName = 'IntuiComboboxSeparator'

export {
  IntuiCombobox,
  IntuiComboboxTrigger,
  IntuiComboboxContent,
  IntuiComboboxInput,
  IntuiComboboxList,
  IntuiComboboxEmpty,
  IntuiComboboxGroup,
  IntuiComboboxItem,
  IntuiComboboxSeparator,
}
